import { z, isoZ } from 'my-zod'
import { attachmentModel } from '../../utilities/attachment'

export const ComplianceCaseLifecycleStatusEnum = z
  .enum(['Draft', 'Void', 'Submitted', 'InProgress', 'ManagerReview', 'Closed'])
  .setEnumLabels({
    Draft: 'Draft',
    Void: 'Void',
    Submitted: 'Submitted',
    InProgress: 'In Progress',
    ManagerReview: 'Manager Review',
    Closed: 'Closed'
  })

export type ComplianceCaseLifecycleStatusEnum = z.infer<typeof ComplianceCaseLifecycleStatusEnum>

export const ComplianceCaseStandardTypeEnum = z
  .enum(['ProgramManagedAssets', 'Safety', 'BrandStandards', 'CustomerExperience'])
  .setEnumLabels({
    ProgramManagedAssets: 'Program Managed Assets',
    Safety: 'Safety',
    BrandStandards: 'Brand Standards',
    CustomerExperience: 'Customer Experience'
  })

export const ComplianceCasePolicyTypeEnum = z.enum([
  'P158 - DOT Helmet is provided to every customer on every rental',
  'P159 - Relevant Outfitter and Catalog Listing published on the Adventures National Website',
  'P160 - Authorized Accessories and Modifications allowed on Vehicles',
  'P161 - Safety Video is presented to every customer prior to rental',
  'P162 - ARRIVAL App is used to facilitate the Rental Experience',
  'Other'
])

export type ComplianceCaseStandard = z.infer<typeof ComplianceCaseStandardTypeEnum>
export const ComplianceCaseStandards = ComplianceCaseStandardTypeEnum.Values
export const ComplianceCaseStandardOptions = Object.values(ComplianceCaseLifecycleStatusEnum.Values)

export const ComplianceCasePolicyInfractionTypeEnum = {
  [ComplianceCaseStandards.ProgramManagedAssets]: ['Vehicle Count', 'Disposition', 'Unauthorized Modifications', 'Other'],
  [ComplianceCaseStandards.Safety]: ['Safety Equipment Usage', 'Insurance', 'Other'],
  [ComplianceCaseStandards.BrandStandards]: [
    'Commercial Use',
    'On-Road Use',
    'Competitor Product',
    'Change in Business',
    'Other'
  ],
  [ComplianceCaseStandards.CustomerExperience]: ['Other']
}

type ComplianceCasePolicyInfractionType =
  (typeof ComplianceCasePolicyInfractionTypeEnum)[keyof typeof ComplianceCasePolicyInfractionTypeEnum][number]

const policyInfraction = z.enum(
  Object.values(ComplianceCasePolicyInfractionTypeEnum).flat() as [
    ComplianceCasePolicyInfractionType,
    ...ComplianceCasePolicyInfractionType[]
  ]
)

export const complianceCaseModel = z
  .object({
    shortId: z.string(),
    outfitterId: z.number().min(1, 'Required').setLabel('Outfitter Name'),
    ownerUserId: z.number().setLabel('Case Owner'),
    createdUserId: z.number().min(1, 'Required').setLabel('Created By'),
    subject: z.string().nonempty().setLabel('Subject'),
    notes: z.string(),
    lifecycleStatus: ComplianceCaseLifecycleStatusEnum,
    standard: ComplianceCaseStandardTypeEnum.nullable(),
    policyNumberAndName: ComplianceCasePolicyTypeEnum.nullable(),
    policyInfraction: policyInfraction.nullable(),
    infractionDate: isoZ.date().nullable().setLabel('Date of Infraction'),
    outfitterNotifiedDate: isoZ.date().nullable(),
    resolutionDate: isoZ.date().nullable(),
    infractionDescription: z.string().max(512).nullable(),
    description: z.string().nonempty('Please provide a description.'),
    recommendationNote: z.string().nullable(),
    resolution: z.string(),
    attachments: attachmentModel.array(),
    lastModifiedByUserId: z.number().setLabel('Last Modified By'),
    completionDate: isoZ.instant().nullable().setLabel('Closed Date'),
    createdDate: isoZ.instant(),
    deletedDate: isoZ.instant().nullable(),
    lastModifiedDate: isoZ.instant()
  })
  .setCFV({
    infractionDateRequired(val) {
      if (!val.infractionDate) {
        return {
          message: 'Please provide date of infraction.',
          path: ['infractionDate']
        }
      }
    }
  })

export type complianceCaseModel = z.infer<typeof complianceCaseModel>
